import { EmployeeOption } from '@/modules/shared/hr-lookups';
import { PmReview, PmReviewDetail, REVIEWER_GROUPS } from '@/modules/performance/hr-review-core';
import { CategoryScore, PerformanceDashboard, QuantitativeMetric, ReviewerGroupScore } from '../types/dashboard.types';
import { calculateQualitative } from './qualitative';
import {
  getFinalScore,
  getPerformanceAxis,
  getPotentialAxis,
  getRatingBandLabel,
  IMPROVEMENT_CATEGORY_THRESHOLD,
  QuantitativeRow,
  ScoringConfig,
  scoreQualitative,
  scoreQuantitative,
  STRENGTH_CATEGORY_THRESHOLD,
} from './scoring-engine';

export interface BuildDashboardInput {
  review: PmReview;
  employee?: EmployeeOption;
  managerName?: string;
  completedDetails: Pick<PmReviewDetail, 'ReviewType' | 'Category' | 'PmFormJson'>[];
  quantitativeRows: QuantitativeRow[];
  config: ScoringConfig;
}

export const buildPerformanceDashboard = ({
  review,
  employee,
  managerName,
  completedDetails,
  quantitativeRows,
  config,
}: BuildDashboardInput): PerformanceDashboard => {
  const qualitative = scoreQualitative(calculateQualitative(completedDetails, config.normalizedMaxScale), config);
  const quantitative = scoreQuantitative(quantitativeRows, config);

  const finalScore = getFinalScore(qualitative.score, quantitative.score, quantitative.isEligible);
  const scored = qualitative.categories.filter((category) => category.weight > 0);

  const categoryScores: CategoryScore[] = qualitative.categories.map((category) => ({
    Category: category.category,
    Score: category.weightedAverage,
    MaxScore: config.normalizedMaxScale,
  }));

  const metrics: QuantitativeMetric[] = quantitative.metrics.map((metric) => ({
    Metric: metric.label,
    Target: metric.target,
    Actual: metric.actual,
    AchievementPercent: metric.achievementPercent,
    Score: metric.score,
  }));

  // One row per reviewer group that submitted: its own per-topic scores and its topic-weighted average.
  const reviewerGroupScores: ReviewerGroupScore[] = REVIEWER_GROUPS.filter(
    (group) => qualitative.effectiveGroupWeights[group] > 0
  ).map((group) => ({
    ReviewerGroup: group,
    CategoryScoresJson: JSON.stringify(
      qualitative.categories.map((category) => ({
        Category: category.category,
        Score: category.byGroup[group] ?? 0,
        MaxScore: config.normalizedMaxScale,
      }))
    ),
    WeightedAverage: qualitative.categories.reduce((sum, category) => sum + (category.byGroup[group] ?? 0) * category.weight, 0),
  }));

  // Legacy "AI review summary" wins when present; otherwise topics are classed by the same thresholds the widgets use.
  const derivedStrengths = scored.filter((c) => c.weightedAverage >= STRENGTH_CATEGORY_THRESHOLD).map((c) => c.category);
  const derivedImprovements = scored
    .filter((c) => c.weightedAverage > 0 && c.weightedAverage < IMPROVEMENT_CATEGORY_THRESHOLD)
    .map((c) => c.category);
  const strengths = review.AiStrengthsSummary?.length ? review.AiStrengthsSummary : derivedStrengths;
  const improvements = review.AiAreasForImprovementSummary?.length ? review.AiAreasForImprovementSummary : derivedImprovements;

  return {
    ItemId: review.ItemId,
    PmReviewId: review.ItemId,
    UserId: review.ReviewForId,
    EmployeeName: employee?.DisplayName ?? review.Name,
    Designation: employee?.Designation,
    DepartmentName: employee?.DepartmentName,
    ManagerName: managerName,
    ReviewCycleLabel: review.CreatedDate?.slice(0, 4) ?? '',
    FinalScore: finalScore,
    QualitativeScore: qualitative.score,
    QuantitativeScore: quantitative.score,
    PerformanceAxis: getPerformanceAxis(qualitative.categories, quantitative.score, quantitative.isEligible, config.normalizedMaxScale),
    PotentialAxis: getPotentialAxis(qualitative.categories, config.normalizedMaxScale),
    RatingBandLabel: getRatingBandLabel(finalScore),
    PortfolioRevenueActual: quantitative.isEligible ? quantitative.revenueActual : undefined,
    CategoryScoresJson: JSON.stringify(categoryScores),
    QuantitativeMetricsJson: JSON.stringify(metrics),
    ReviewerGroupScoresJson: JSON.stringify(reviewerGroupScores),
    StrengthsJson: JSON.stringify(strengths),
    ImprovementAreasJson: JSON.stringify(improvements),
    ReviewerGroupsSubmitted: qualitative.reviewerGroupsSubmitted,
    ReviewerGroupsTotal: qualitative.reviewerGroupsTotal,
    IsQuantitativeEligible: quantitative.isEligible,
  };
};
